import { makeStyles, mergeClasses } from '@fluentui/react-components';
import type { ReactNode } from 'react';

import { palette, tokens } from '../theme';

export type SegmentedOption<T extends string> = { value: T; label: string; icon: ReactNode };

export type SegmentedToggleProps<T extends string> = {
    value: T;
    onChange: (next: T) => void;
    options: SegmentedOption<T>[];
    ariaLabel: string;
};

export const SegmentedToggle = <T extends string>({ value, onChange, options, ariaLabel }: SegmentedToggleProps<T>) => {
    const classes = useStyles();
    return (
        <div className={classes.toggle} role="group" aria-label={ariaLabel}>
            {options.map((opt) => (
                <button
                    key={opt.value}
                    type="button"
                    aria-pressed={value === opt.value}
                    className={mergeClasses(classes.toggleBtn, value === opt.value && classes.toggleBtnActive)}
                    onClick={() => onChange(opt.value)}
                >
                    {opt.icon}
                    <span>{opt.label}</span>
                </button>
            ))}
        </div>
    );
};

const useStyles = makeStyles({
    toggle: {
        // Equal-width columns that size to the widest segment — works for
        // any option count (2 for view, 3 for appearance) without a fixed width.
        display: 'inline-grid',
        gridAutoFlow: 'column',
        gridAutoColumns: '1fr',
        height: '36px',
        backgroundColor: palette.panelBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: palette.pillRadius,
        boxShadow: palette.softShadowSm,
        overflow: 'hidden',
    },
    toggleBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        minWidth: '76px',
        border: 'none',
        borderLeft: `1px solid ${palette.cardBorder}`,
        background: 'transparent',
        padding: '0 10px',
        fontFamily: 'inherit',
        fontSize: '12.5px',
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground2,
        cursor: 'pointer',
        transition: 'background-color 160ms ease, color 160ms ease',
        '&:first-child': {
            borderLeft: 'none',
        },
        '&:hover': {
            backgroundColor: palette.accentBg,
            color: palette.accentText,
        },
        '&:focus-visible': {
            outline: `2px solid ${palette.accent}`,
            outlineOffset: '-2px',
        },
    },
    toggleBtnActive: {
        backgroundColor: palette.accent,
        color: palette.primaryText,
        '&:hover': {
            backgroundColor: palette.accentHover,
            color: palette.primaryText,
        },
    },
});
